// web-commands.ts — the PER-APP half of the web shape: maps oriora_encore's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Show } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const SHOWS = "shows";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_shows: ({ from, to, limit }) => {
      let rows = db.all(SHOWS) as Array<{ id: string; seen_on?: string; created_at?: string }>;
      if (from) rows = rows.filter((r) => String(r.seen_on ?? "") >= String(from));
      if (to) rows = rows.filter((r) => String(r.seen_on ?? "") <= String(to));
      rows.sort((a, b) => {
        const s = String(b.seen_on ?? "").localeCompare(String(a.seen_on ?? ""));
        return s !== 0 ? s : String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
      });
      const lim = typeof limit === "number" ? limit : Number(limit ?? 50000);
      return rows.slice(0, lim);
    },
    get_show: ({ id }) => db.get(SHOWS, String(id)),
    create_show: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row = {
        id: uuid(),
        seen_on: String(i.seen_on ?? now.slice(0, 10)),
        artists: Array.isArray(i.artists) ? (i.artists as string[]) : [],
        venue: String(i.venue ?? ""),
        city: String(i.city ?? ""),
        cost: typeof i.cost === "number" ? i.cost : null,
        currency: (String(i.currency ?? "USD").toUpperCase() || "USD"),
        companions: Array.isArray(i.companions) ? (i.companions as string[]) : [],
        setlist_notes: String(i.setlist_notes ?? ""),
        rating: typeof i.rating === "number" ? i.rating : 0,
        is_festival: Boolean(i.is_festival ?? false),
        photo: "",
        created_at: now,
        updated_at: now,
      };
      return db.insert(SHOWS, row);
    },
    update_show: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const next: Record<string, unknown> = { updated_at: nowIso() };
      for (const k of ["artists", "seen_on", "venue", "city", "currency", "companions", "setlist_notes", "rating", "is_festival"]) {
        if (k in p) next[k] = p[k];
      }
      // cost only applies when cost_set === true (mirrors native ShowPatch).
      if (p.cost_set === true) next.cost = typeof p.cost === "number" ? p.cost : null;
      db.update(SHOWS, String(id), next);
      return null;
    },
    delete_show: ({ id }) => {
      db.remove(SHOWS, String(id));
      return null;
    },
    // Attach a photo to a show — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_show_photo: ({ id, photo }) => {
      db.update(SHOWS, String(id), { photo: String(photo ?? ""), updated_at: nowIso() });
      return null;
    },
    // Derived — native computes rich signals in signals.rs. Web shell = valid-but-empty
    // so the UI never crashes; the AI recap is degraded until the signal logic is ported.
    // Derived — Rust in signals.rs, faithfully ported for web in signals-web.ts.
    // This used to be a valid-but-EMPTY shell, so the "Year in Live Music" recap — the app's one
    // billed feature — was handed a ledger of zeros on the web surface.
    compute_signals: ({ today }) => computeSignalsWeb(
      db.all(SHOWS) as unknown as Show[],
      String(today ?? new Date().toISOString().slice(0, 10)),
    ),
  };
}
